import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "reactstrap";
import useNavigation from "shared/utils/useNavigation";
import { DEFAULT_AVATAR } from "../../../../shared/utils/matrix/matrixRoomUtils";
import { fetchWalletBalanceAction } from "../../../../shared/actions/creditAction";
import {
  formatCreditsValue,
  formatHandle,
  getCallRequestPricingDetails,
  isWalletBalancePending,
  subscriberHasMinimumCreditsForCall,
} from "./utils/callFormatUtils";
import { isVideoCallKind, navigateToCallRoute, normalizeCallKind } from "./constant";

const CallRequestModal = ({
  isOpen,
  onClose,
  onAddCredits,
  creatorInfo,
  callKind = "voice",
  roomId,
  isLoading = false,
}) => {
  const navigate = useNavigation();
  const dispatch = useDispatch();
  const walletBalance = useSelector((state) => state.credit?.walletBalance);
  const walletBalanceLoading = useSelector((state) => state.credit?.walletBalanceLoading);
  const walletBalanceError = useSelector((state) => state.credit?.walletBalanceError);
  const currentUser = useSelector((state) => state.user?.user);
  const credits = Number(walletBalance?.credits);
  const creditsDisplay = Number.isFinite(credits)
    ? formatCreditsValue(credits)
    : "—";

  const kind = normalizeCallKind(callKind);
  const isVideo = isVideoCallKind(kind);
  const modalTitle = isVideo ? "Video Call Request" : "Voice Call Request";
  const callTypeLabel = isVideo ? "Video Call" : "Voice Call";

  const pricingResult = useMemo(
    () => getCallRequestPricingDetails(creatorInfo, kind),
    [creatorInfo, kind]
  );
  const allowNoCreditForTesting =
    String(process.env.REACT_APP_ALLOW_NO_CREDIT_FOR_TESTING).toLowerCase() === "true";
  const pricingOk = pricingResult.ok;
  const pricingError = pricingOk ? null : pricingResult.error;
  const pricing = pricingOk ? pricingResult : null;

  const walletBalancePending = useMemo(
    () => isWalletBalancePending(walletBalance, walletBalanceLoading, walletBalanceError),
    [walletBalance, walletBalanceLoading, walletBalanceError]
  );

  useEffect(() => {
    if (!isOpen || !pricingOk) return;
    const subscriberId = currentUser?._id || currentUser?.id;
    dispatch(fetchWalletBalanceAction(subscriberId));
  }, [isOpen, pricingOk, dispatch, currentUser?._id, currentUser?.id]);

  const canSendRequest =
    pricingOk &&
    (allowNoCreditForTesting ||
      subscriberHasMinimumCreditsForCall(pricingResult, credits, walletBalancePending));
  const insufficientCredits =
    !allowNoCreditForTesting &&
    pricingOk &&
    !walletBalancePending &&
    Number.isFinite(credits) &&
    credits < pricingResult.minCostCredits;

  const avatarSrc =
    typeof creatorInfo?.profile_picture === "string" && creatorInfo.profile_picture.trim() !== ""
      ? creatorInfo.profile_picture
      : DEFAULT_AVATAR;
  const handle = formatHandle(creatorInfo?.username);

  const rateDisplayLine = pricing
    ? pricing.pricingModel === "flat_rate"
      ? `${formatCreditsValue(pricing.flatRateCredits)} credits / ${pricing.minDurationMins} min`
      : `${formatCreditsValue(pricing.ratePerMinuteCredits)} credits/min`
    : "—";
  const requiredCredits = pricing
    ? pricing.pricingModel === "flat_rate"
      ? pricing.flatRateCredits
      : pricing.minCostCredits
    : null;
  const hasMinimumDuration = Boolean(pricing?.hasMinimumDuration);
  const minimumDurationLabel = hasMinimumDuration
    ? `${pricing.minDurationMins} minute${pricing.minDurationMins === 1 ? "" : "s"}`
    : "No minimum";
  const minimumCostLabel = "Required";

  const billingInfoText = useMemo(() => {
    if (!pricing) return null;
    if (pricing.pricingModel === "flat_rate") {
      return "Flat-rate sessions are charged in time blocks when the call begins. An additional charge will apply when a new time block is reached during the call.";
    }
    if (hasMinimumDuration) {
      return "Billing starts when the creator joins the call. If you end the call early, the required credits still apply. If the creator ends the call, you only pay for the time spent.";
    }
    return "Billing begins once the creator joins the call.";
  }, [pricing, hasMinimumDuration]);

  const callOpts = roomId ? { roomId } : {};

  const handleSendRequest = () => {
    if (pricingError || !canSendRequest) return;
    onClose?.();
    void navigateToCallRoute(navigate, kind, callOpts);
  };

  const handleSendRequestKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleSendRequest();
    }
  };

  return (
    <Modal isOpen={isOpen} centered className="cm_call_info_modal">
      <div className="cm_call_info_header d-flex align-items-center justify-center mb-3">
        <div className="m_p_icon">
          <i className={isVideo ? "fa-solid fa-video" : "fa-solid fa-phone"} aria-hidden />
        </div>
        <h5 className="m-0">{modalTitle}</h5>
      </div>

      {isLoading ? (
        <div className="d-flex flex-column align-items-center justify-content-center py-5">
          <div className="spinner-border text-light mb-3" role="status" aria-label="Loading call details" />
          <div className="small text-muted">Fetching latest call details...</div>
        </div>
      ) : (
        <>
      <div className="d-flex align-items-center justify-content-between mb-3 p-3 cm_call_info_card cm_call_info_card_1 gap-3">
        <div className="cm_call_info_profile flex-shrink-0">
          <img src={avatarSrc} className="pp_img" alt="" />
          <div className="fw-bold text-truncate cm_call_info_handle">{handle}</div>
        </div>
        <div className="cm_call_rate_summary text-end flex-shrink-0">
          <div className="cm_call_rate_label">Rate</div>
          <div className="cm_call_rate_value">{rateDisplayLine}</div>
        </div>
      </div>

      <div className="d-flex justify-content-around align-items-center gap-3 mb-3">
        <div className="credits_plus_chip_lg">
          <span>
            <i className="fa-solid fa-coins" aria-hidden />
          </span>
          Credits: {creditsDisplay}
        </div>
        <span className="jh_btn_pink" onClick={onAddCredits} role="button" tabIndex={0}>
          Add Credits
        </span>
      </div>

      <div className="p-3 mb-3 cm_call_info_card cm_call_info_card_2">
        {pricingError ? (
          <div className="text-danger" role="alert">
            {pricingError}
          </div>
        ) : (
          <>
            <div className="d-flex justify-content-between mb-3">
              <span>Call Type</span>
              <span className="fw-bold">{callTypeLabel}</span>
            </div>

            <div className="d-flex justify-content-between mb-3">
              <span>{pricing.pricingModel === "flat_rate" ? "Flat rate" : "Rate per minute"}</span>
              <span className="fw-bold">
                {pricing.pricingModel === "flat_rate" ? rateDisplayLine : pricing.ratePerMinuteRow}
              </span>
            </div>

            {pricing.pricingModel !== "flat_rate" && (
              <div className="d-flex justify-content-between mb-3">
                <span>Minimum duration</span>
                <span className="fw-bold">{minimumDurationLabel}</span>
              </div>
            )}

            <div className="jj_divider"></div>

            <div className="d-flex justify-content-between">
              <span>{minimumCostLabel}</span>
              <span className="fw-bold">
                {formatCreditsValue(requiredCredits ?? pricing.minCostCredits)} credits
              </span>
            </div>

            {insufficientCredits && (
              <div className="cm_call_req_alert cm_call_req_alert_error small mt-3" role="alert">
                Add {formatCreditsValue(requiredCredits ?? pricing.minCostCredits)} credits to
                request this call.
              </div>
            )}
            {pricingOk && walletBalancePending && (
              <div className="cm_call_req_alert cm_call_req_alert_muted small mt-3">
                Checking your credit balance…
              </div>
            )}
            {pricingOk && !walletBalancePending && !Number.isFinite(credits) && (
              <div className="cm_call_req_alert cm_call_req_alert_error small mt-3" role="alert">
                {walletBalanceError
                  ? String(walletBalanceError)
                  : "Could not read your credit balance. Try again or add credits."}
              </div>
            )}
          </>
        )}
      </div>

      <div className="p-3 mm_warinig_box mb-3">
        <i className="fa-solid fa-circle-info" aria-hidden />
        {billingInfoText}
      </div>

      <div className="d-flex justify-content-between gap-3">
        <span className="jh_btn_gray min_135 text-no-wrap" onClick={onClose} role="button" tabIndex={0}>
          Cancel
        </span>
        <span
          className={`jh_btn_pink min_135 text-no-wrap${!canSendRequest ? " opacity-50" : ""}`}
          role="button"
          tabIndex={canSendRequest ? 0 : -1}
          aria-disabled={!canSendRequest}
          onClick={handleSendRequest}
          onKeyDown={canSendRequest ? handleSendRequestKeyDown : undefined}
        >
          Send Request
        </span>
      </div>
        </>
      )}
    </Modal>
  );
};

export default CallRequestModal;
